import {
  alpha,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import { styled } from '@mui/material/styles';
import { formatDistanceToNowStrict } from 'date-fns';
import { useNotificationStore } from '../../../../../store/notifications.store';

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

function formatNotificationAge(createdAt: string): string {
  return formatDistanceToNowStrict(new Date(createdAt), { addSuffix: true })
    .replace(/\bseconds?\b/, 'sec')
    .replace(/\bminutes?\b/, 'min')
    .replace(/\bhours?\b/, 'hr')
    .replace(/\bdays?\b/, 'day')
    .replace(/\bmonths?\b/, 'mo')
    .replace(/\byears?\b/, 'yr');
}

function HeaderNotifications() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const notifications = useNotificationStore((state) => state.notifications);
  const markNotificationRead = useNotificationStore(
    (state) => state.markNotificationRead
  );
  const markAllNotificationsRead = useNotificationStore(
    (state) => state.markAllNotificationsRead
  );
  const markAllNotificationsUnread = useNotificationStore(
    (state) => state.markAllNotificationsUnread
  );
  const dismissNotification = useNotificationStore(
    (state) => state.dismissNotification
  );
  const loadEmailActivity = useNotificationStore(
    (state) => state.loadEmailActivity
  );
  const retryEmailNotification = useNotificationStore(
    (state) => state.retryEmailNotification
  );
  const clearNotifications = useNotificationStore(
    (state) => state.clearNotifications
  );
  const addNotification = useNotificationStore(
    (state) => state.addNotification
  );
  const unreadNotifications = notifications.filter(
    (notification) => !notification.read
  );

  useEffect(() => {
    loadEmailActivity();
    const interval = window.setInterval(loadEmailActivity, 60000);
    return () => window.clearInterval(interval);
  }, [loadEmailActivity]);

  const handleOpen = (): void => {
    setOpen(true);
    loadEmailActivity();
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleRowClick = (notificationId: string) => {
    markNotificationRead(notificationId);
  };

  return (
    <>
      <Tooltip arrow title="Notifications">
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            badgeContent={unreadNotifications.length}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box sx={{ p: 2, minWidth: 360, maxWidth: 'calc(100vw - 32px)' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5">Notifications</Typography>
            {notifications.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {unreadNotifications.length} unread
              </Typography>
            )}
          </Box>
          {notifications.length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={0.5}
              sx={{ mt: 1 }}
            >
              <Button
                size="small"
                onClick={markAllNotificationsRead}
                disabled={!unreadNotifications.length}
              >
                Mark all read
              </Button>
              <Button size="small" onClick={markAllNotificationsUnread}>
                Mark all unread
              </Button>
              <Button size="small" color="error" onClick={clearNotifications}>
                Clear all
              </Button>
            </Box>
          )}
        </Box>
        <Divider />
        <List sx={{ p: 0, minWidth: 360 }}>
          {notifications.length === 0 ? (
            <ListItem sx={{ p: 2 }}>
              <ListItemText
                primary="No notifications yet."
                secondary="Transaction activity will appear here."
              />
            </ListItem>
          ) : (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                alignItems="flex-start"
                sx={{
                  p: 2,
                  display: 'block',
                  bgcolor: notification.read ? 'transparent' : 'action.hover'
                }}
                onClick={() => handleRowClick(notification.id)}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                >
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {notification.title} (
                    {formatNotificationAge(notification.createdAt)})
                  </Typography>
                  {!notification.read && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'primary.main', fontWeight: 700 }}
                    >
                      Unread
                    </Typography>
                  )}
                </Box>
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {notification.message}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {notification.severity === 'error' &&
                    notification.id.startsWith('email-') && (
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          void retryEmailNotification(notification.id).catch(
                            () => {
                              addNotification({
                                title: 'Retry failed',
                                message:
                                  'The email could not be reprocessed. Please try again later.',
                                severity: 'error'
                              });
                            }
                          );
                        }}
                      >
                        Retry
                      </Button>
                    )}
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        notification.severity === 'error'
                          ? 'error.main'
                          : notification.severity === 'success'
                          ? 'success.main'
                          : notification.severity === 'warning'
                          ? 'warning.main'
                          : 'text.secondary',
                      fontWeight: 600
                    }}
                  >
                    {notification.severity.toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      dismissNotification(notification.id);
                    }}
                  >
                    Dismiss
                  </Typography>
                </Box>
              </ListItem>
            ))
          )}
        </List>
      </Popover>
    </>
  );
}

export default HeaderNotifications;
